"use client";

import { useEffect, useRef } from "react";
import { varToColor } from "preline/helpers/shared";
import { baueBalkenOptionen, type BalkenDaten, type DiagrammFarben } from "./balken-optionen";

/** Preline-Token → Farbwert (wie buildChart im Block über varToColor). */
function farbe(token: string, el: Element, ersatz: string): string {
  return varToColor(token, el) ?? ersatz;
}

/**
 * ApexCharts-Säulendiagramm (Bibliothek lokal aus apexcharts@4.7.0, keine CDN-Ladung).
 * Optionen entstehen ausschließlich in `baueBalkenOptionen` (zentrales Escaping).
 * Für Screenreader steht dieselbe Reihe als Liste daneben (React rendert die Texte sicher).
 */
export function Balkendiagramm({ id, daten, beschriftung }: { id: string; daten: BalkenDaten; beschriftung: string }) {
  const ziel = useRef<HTMLDivElement>(null);
  const schluessel = JSON.stringify(daten);

  useEffect(() => {
    const el = ziel.current;
    if (!el) return;
    let abgebrochen = false;
    let diagramm: { render: () => Promise<void>; destroy: () => void } | null = null;
    const farben: DiagrammFarben = {
      primaer: farbe("--chart-colors-primary", el, "currentColor"),
      gitter: farbe("--chart-colors-grid-border", el, "currentColor"),
      xAchse: farbe("--chart-colors-xaxis-labels", el, "currentColor"),
      yAchse: farbe("--chart-colors-yaxis-labels", el, "currentColor"),
      schrift: getComputedStyle(el).fontFamily,
    };
    void import("apexcharts").then(({ default: ApexCharts }) => {
      if (abgebrochen) return;
      diagramm = new ApexCharts(el, baueBalkenOptionen(JSON.parse(schluessel) as BalkenDaten, farben));
      void diagramm.render();
    });
    return () => {
      abgebrochen = true;
      diagramm?.destroy();
    };
  }, [schluessel]);

  return (
    <>
      {/* Preline: data-display/data-display-sub-analytics-kpis/data-display-cat-bar-charts/progress-row-bar-chart-card */}
      <div id={id} ref={ziel} className="min-h-92 -mx-2" role="img" aria-label={beschriftung} />
      <ul className="sr-only">
        {daten.kategorien.map((k, i) => (
          <li key={k}>
            {k}: {daten.werte[i]}
          </li>
        ))}
      </ul>
    </>
  );
}
